import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { createRoot } from "react-dom/client";
import type { ReactNode } from "react";

interface RenderResult {
  blob: Blob;
  url: string;
  filename: string;
}

interface RenderOptions {
  filename: string;
  /**
   * Pixel scale for html2canvas. Higher = sharper but bigger files.
   * 2x matches what the old desktop app shipped with.
   */
  scale?: number;
}

/**
 * Render a React element into a hidden DOM node, screenshot it with
 * html2canvas, and embed the bitmap into an A4 jsPDF. The output mirrors
 * the original BillForge desktop look exactly because the PDF is literally
 * a picture of the print-styled DOM.
 *
 * Multi-page handling: if the rendered canvas is taller than a single A4
 * page, the image is sliced vertically across as many pages as needed.
 *
 * Callers MUST revoke the returned object URL with `URL.revokeObjectURL`
 * once they're done with it (e.g. when the preview modal closes).
 */
export async function renderReactToPdf(
  element: ReactNode,
  { filename, scale = 2 }: RenderOptions,
): Promise<RenderResult> {
  // Off-screen container — far enough left to be invisible but still laid
  // out by the browser so html2canvas can measure it.
  const host = document.createElement("div");
  host.setAttribute("data-pdf-host", "true");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "0";
  host.style.background = "#ffffff";
  host.style.zIndex = "-1";
  document.body.appendChild(host);

  const root = createRoot(host);
  try {
    root.render(element);

    // Give React + fonts + Tailwind one paint cycle to settle.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    // Extra short delay so web fonts that are still loading on first render
    // get a chance to swap in before we screenshot.
    await new Promise<void>((resolve) => setTimeout(resolve, 120));

    const target = host.firstElementChild as HTMLElement | null;
    if (!target) {
      throw new Error("Print component failed to render");
    }

    const canvas = await html2canvas(target, {
      scale,
      backgroundColor: "#ffffff",
      useCORS: true,
      // The off-screen host is positioned negatively; html2canvas needs the
      // explicit dimensions to capture the element correctly.
      width: target.offsetWidth,
      height: target.offsetHeight,
      windowWidth: target.offsetWidth,
      windowHeight: target.offsetHeight,
    });

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;

    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - margin * 2;

    // Fit the canvas width to the printable area, then slice vertically.
    const scaleRatio = usableWidth / canvas.width;
    const renderedFullHeight = canvas.height * scaleRatio;

    if (renderedFullHeight <= usableHeight) {
      // Whole thing fits on one page — simple path.
      doc.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        margin,
        margin,
        usableWidth,
        renderedFullHeight,
      );
    } else {
      // Multi-page: copy a rectangle from the source canvas into a per-page
      // canvas, then drop each into a fresh jsPDF page.
      const sliceHeightPx = Math.floor(usableHeight / scaleRatio);
      let consumed = 0;
      let pageIndex = 0;
      while (consumed < canvas.height) {
        const remaining = canvas.height - consumed;
        const thisSlicePx = Math.min(sliceHeightPx, remaining);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = thisSlicePx;
        const ctx = pageCanvas.getContext("2d");
        if (!ctx) throw new Error("Could not get 2D canvas context");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          consumed,
          canvas.width,
          thisSlicePx,
          0,
          0,
          canvas.width,
          thisSlicePx,
        );
        const renderedH = thisSlicePx * scaleRatio;
        if (pageIndex > 0) doc.addPage();
        doc.addImage(
          pageCanvas.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          usableWidth,
          renderedH,
        );
        consumed += thisSlicePx;
        pageIndex += 1;
      }
    }

    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    return { blob, url, filename };
  } finally {
    // Always tear down the off-screen root, even on errors, to avoid leaks.
    root.unmount();
    host.remove();
  }
}

export function safePdfFilename(prefix: string, identifier: string, suffix = "") {
  const safe = identifier.replace(/[^A-Za-z0-9_\-]/g, "_") || "Draft";
  const tail = suffix ? `-${suffix}` : "";
  return `${prefix}-${safe}${tail}.pdf`;
}
